import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { ReactNode } from 'react';
import {
    Button,
    Card,
    FlexBox,
    H2,
    Link,
    TabContent,
    Tabs,
    TabsContents,
    Typography,
} from '@vp/swan';
import { useLogger, useUserContext } from '@vp/ubik-context';
import { useOnUserIdentityUpdate } from '../../auth/useOnUserIdentityUpdate';
import { useEnvContext } from '../../utils/environmentContext';
import { removePathQueryParams } from '../../utils/removePathQueryParams';
import { useTrack, useTrackNavigationClicked } from './../../utils/analytics';
import { useAbortController } from './../../utils/useAbortController';
import BusinessNameInput from './BusinessNameInput';
import { getGalleryLogoExamples } from './gallery-logo-examples';
import ImageGallery from './ImageGallery';
import IndustryTabsHeaders, { industryHeadings } from './IndustryTabsHeaders';
import { fetchLogomationSessionId } from './logomation-session';
import type { Logger } from '@vp/ubik-logging';
import type { Identity } from './../../auth/useOnUserIdentityUpdate';
import type { GalleryExamples, GalleryRequestConfig, LogomakerLogo } from './gallery-logo-examples';
import type { IndustryKey } from './IndustryTabsHeaders';

interface LogomakerAnywhereProps {
    keyword?: string;
    keywordLongName?: string;
    title?: string;
    description?: string | ReactNode;
    backgroundColor?: 'warm-white' | 'standard';
    customCtaText?: string;
    initialBusinessName?: string;
    industryKeywords?: string[];
    showBusinessNameInput?: boolean;
    isModalLayout?: boolean;
}

const LogomakerAnywhere = ({
    keyword,
    keywordLongName,
    title,
    description,
    backgroundColor = 'warm-white',
    customCtaText,
    initialBusinessName,
    industryKeywords,
    showBusinessNameInput = true,
    isModalLayout = false,
}: LogomakerAnywhereProps) => {
    const { t } = useTranslation();
    const logger: Logger = useLogger();
    const { ENV_LOGOMATION_BASE_URL } = useEnvContext();

    const defaultBusinessName = t(
        'Vista Logos > Logomaker Anywhere > Text Input > Default business name.value',
    );

    const [galleryLogoExamples, setGalleryLogoExamples] = useState<
        GalleryExamples | LogomakerLogo[]
    >(
        keyword
            ? []
            : (Object.fromEntries(
                  industryHeadings.map((k: IndustryKey) => [k, []]),
              ) as unknown as GalleryExamples),
    );
    const [apiError, setApiError] = useState(false);
    const [logomationSessionId, setLogomationSessionId] = useState('');
    const [businessName, setBusinessName] = useState<string>(
        initialBusinessName ?? defaultBusinessName,
    );
    const [selectedIndustry, setSelectedIndustry] = useState<IndustryKey>(industryHeadings[0]);
    const getAbortController = useAbortController();

    const identity: Identity | undefined = useOnUserIdentityUpdate(async (identity: Identity) => {
        setApiError(false);
        const sessionId = await fetchLogomationSessionId(identity, logger, ENV_LOGOMATION_BASE_URL);

        if (sessionId) {
            // Write session ID to localStorage so that it can be read by Logomaker Studio
            try {
                localStorage.setItem('logomakerAnywhereLogomationSessionId', sessionId);
            } catch (error) {
                logger.error({
                    name: 'Logomaker Anywhere: Error while recording Logomation session ID',
                    message: (error as Error).message,
                });
                // If we are unable to write session ID to localStorage raise error
                // to prevent user from proceeding to Logomaker Studio
                setApiError(true);
            }
            setLogomationSessionId(sessionId);
            fetchAndSetLogomationImages(identity, sessionId, businessName);
        } else {
            setApiError(true);
        }
    });

    const logomakerWizardCtaRef = useTrackNavigationClicked(
        'LogomakerAnywhere',
        'LogomakerAnywhere',
        '/logomaker/wizard',
        'createYourFreelogo',
    );

    const businessNameGeneratorRef = useTrackNavigationClicked(
        'LogomakerAnywhere',
        'LogomakerAnywhere',
        '/business-name-generator',
        'businessName',
    );

    const track = useTrack();
    const pathName = useUserContext().forwardedPath || '';
    const currentPagePath = removePathQueryParams(pathName);

    function trackApiError(requestBody: GalleryRequestConfig) {
        track('Logomaker Anywhere Api Error', {
            pageSection: 'LogomakerAnywhere',
            category: 'LogomakerAnywhere',
            label: 'Content:LogomakerAnywhere',
            eventDetail: `${currentPagePath};${currentPagePath};LogomakerAnywhere;apiError`,
            navigationDetail: 'apiError',
            companyName: requestBody.companyName,
            industryName: requestBody.industryName,
            industryKeywords: requestBody.industryKeywords,
        });
    }

    const fetchAndSetLogomationImages = async (
        identity: Identity,
        sessionId: string,
        companyName?: string,
    ): Promise<void> => {
        // Cancel any in-flight Logomation requests
        const abortController = getAbortController();
        const wordsInName = (companyName || '').toLowerCase().split(' ');

        // Reset logo gallery
        if (keyword) {
            setGalleryLogoExamples([]);
        } else {
            setGalleryLogoExamples(
                Object.fromEntries(
                    industryHeadings.map((k: IndustryKey) => [k, []]),
                ) as unknown as GalleryExamples,
            );
        }

        if (keyword) {
            // Only fetch logo designs once on inspiration keyword page
            const galleryRequestConfig: GalleryRequestConfig = {
                companyName: companyName || defaultBusinessName,
                industryName: keyword || '',
                industryKeywords: [keyword, ...wordsInName, ...(industryKeywords || [])],
            };
            for await (const logo of getGalleryLogoExamples(
                galleryRequestConfig,
                identity,
                sessionId,
                abortController,
                trackApiError,
                logger,
                ENV_LOGOMATION_BASE_URL,
            )) {
                setGalleryLogoExamples((previousLogos) => [
                    ...(previousLogos as LogomakerLogo[]),
                    logo,
                ]);
            }
        } else {
            industryHeadings.forEach(async (industryName: IndustryKey) => {
                const galleryRequestConfig: GalleryRequestConfig = {
                    companyName: companyName || defaultBusinessName,
                    industryName,
                    industryKeywords: wordsInName,
                };

                for await (const logo of getGalleryLogoExamples(
                    galleryRequestConfig,
                    identity,
                    sessionId,
                    abortController,
                    trackApiError,
                    logger,
                    ENV_LOGOMATION_BASE_URL,
                )) {
                    setGalleryLogoExamples((previousGallery) => {
                        const newGallery = structuredClone(previousGallery) as GalleryExamples;
                        newGallery[industryName].push(logo);
                        return newGallery;
                    });
                }
            });
        }
    };

    const handleSubmit = async (businessName: string) => {
        if (!identity) return;
        setBusinessName(businessName);
        fetchAndSetLogomationImages(identity, logomationSessionId, businessName);
    };

    const handleClick = () => {
        // The Logomaker wizard needs this flag to clear the IndexedDB for it's functionality
        sessionStorage.setItem('isReloaded', 'false');
    };

    function trackIndustryTabChange(industry: string) {
        track('Logomaker Anywhere Industry Tab Change', {
            pageSection: 'LogomakerAnywhere',
            category: 'LogomakerAnywhere',
            label: 'Content:LogomakerAnywhere',
            eventDetail: `${currentPagePath};${currentPagePath};LogomakerAnywhere;onIndustryTabChange`,
            navigationDetail: 'onIndustryTabChange',
            industry,
        });
    }

    const defaultTitle = `Get ${keywordLongName ? (/^[aeiou]/i.test(keywordLongName) ? 'an' : 'a') + ` ${keywordLongName} ` : 'a '}
                logo, like, now!`;

    const defaultDescription = (
        <>
            Try our free Logomaker and see your{' '}
            <Link href="/business-name-generator" target="_blank" ref={businessNameGeneratorRef}>
                business name
            </Link>{' '}
            come to life. Our Logomaker will generate dozens of {keyword ? `${keyword} ` : ''}logo
            ideas in seconds, then simply download the logo you like and you’re done.
        </>
    );

    return (
        <Card bgc={backgroundColor} padding={6}>
            <H2 fontSkin="title-section">{title || defaultTitle}</H2>
            <Typography fontSkin="body-standard" marginTop={4} marginBottom={6}>
                {description || defaultDescription}
            </Typography>
            {showBusinessNameInput && <BusinessNameInput onSubmit={handleSubmit} />}
            {!keyword ? (
                <Tabs
                    id="industry-tabs"
                    selectedTabId={selectedIndustry}
                    showDividingLine={false}
                    defaultSelectedTabId={industryHeadings[0]}
                    onRequestTabChange={(requestedTabId) => {
                        setSelectedIndustry(requestedTabId as IndustryKey);
                        trackIndustryTabChange(requestedTabId!);
                    }}
                    aria-label={t(
                        'Vista Logos > Logomaker Anywhere > Industry Tab Aria Label.value',
                    )}
                >
                    <IndustryTabsHeaders />
                    <TabsContents>
                        <TabContent tabId={selectedIndustry}>
                            <ImageGallery
                                businessName={businessName}
                                industry={keyword ? '' : selectedIndustry}
                                logoExamples={
                                    keyword
                                        ? (galleryLogoExamples as LogomakerLogo[])
                                        : (galleryLogoExamples as GalleryExamples)[selectedIndustry]
                                }
                                keywords={keyword ? [keyword] : []}
                                keywordLongName={keywordLongName}
                                showErrorMessage={apiError}
                                isModalLayout={isModalLayout}
                            />
                        </TabContent>
                    </TabsContents>
                </Tabs>
            ) : (
                <ImageGallery
                    businessName={businessName}
                    industry={keyword ? '' : selectedIndustry}
                    logoExamples={
                        keyword
                            ? (galleryLogoExamples as LogomakerLogo[])
                            : (galleryLogoExamples as GalleryExamples)[selectedIndustry]
                    }
                    keywords={keyword ? [keyword] : []}
                    keywordLongName={keywordLongName}
                    showErrorMessage={apiError}
                    isModalLayout={isModalLayout}
                />
            )}
            <FlexBox justifyContent="center">
                <Button
                    skin="primary"
                    render={(props) => (
                        <Link
                            onClick={handleClick}
                            to={t('Vista Logos > Logomaker Anywhere > CTA.contentfulUrl')}
                            className={props.className}
                            ref={logomakerWizardCtaRef}
                        >
                            {props.children}
                        </Link>
                    )}
                >
                    {customCtaText || t('Vista Logos > Logomaker Anywhere > CTA.text')}
                </Button>
            </FlexBox>
        </Card>
    );
};

export default LogomakerAnywhere;
